import { useDispatch } from 'react-redux'
import classNames from 'classnames'
import P from 'components/typography/Paragraph'
import SocialIcon from 'components/misc/SocialIcon'
import Link from 'components/misc/Link'
import styles from './styles/Footer.css'
import headerLvl from 'components/typography/styles/Header.css'
import useUploadsDisallowed from 'hooks/useUploadsDisallowed'
import { PUBLISHER_COUNT } from 'lib/publishers'
import { toggleModal } from 'store/ui/actions'
import SciteLogo from 'public/assets/images/logo.svg?react'
import ResearchSolutionsLogo from 'public/assets/images/rs-logo.svg?react'

const About = () => (
  <div className={styles.aboutContainer}>
    <SciteLogo width='128px' height='41px' aria-label='scite logo' className={styles.logo} />
    <P className={styles.aboutText}>
      Scite is an AI-powered platform that helps researchers discover and evaluate scientific literature through Smart
      Citations, showing whether studies support or contradict a claim. Now part of Research Solutions, Scite has
      indexed 1.6B+ citations, partners with {PUBLISHER_COUNT}+ publishers, and serves 2M users worldwide.
    </P>
    <Contact />
  </div>
)

const Contact = () => (
  <div className={styles.contact}>
    <div className={headerLvl.h6}>Contact Info</div>
    <div>
      <P className={styles.address}>customersupport@researchsolutions.com</P>
      <P className={styles.address}>10624 S. Eastern Ave., Ste. A-614</P>
      <P className={styles.address}>Henderson, NV 89052, USA</P>
    </div>
    <SocialIcons />
  </div>
)

const SocialIcons = () => (
  <div className={styles.iconsLayout}>
    <SocialIcon className={styles.icon} type='x' />
    <SocialIcon className={styles.icon} type='linkedin' />
    <SocialIcon className={styles.icon} type='youtube' />
  </div>
)

const ProductLinks = () => {
  const dispatch = useDispatch()
  const uploadsDisallowed = useUploadsDisallowed()
  return (
    <div className={styles.productContainer}>
      <div>
        <div className={headerLvl.h6}>Product</div>
        <div className={styles.products}>
          <Link className={styles.link} to='/assistant' aria-label='Scite Assistant - links to /assistant'>
            Scite Assistant
          </Link>
          <Link className={styles.link} to='/search' aria-label='Search - links to /search'>
            Search
          </Link>
          {!uploadsDisallowed && (
            <Link
              className={styles.link}
              to='/?referenceCheck=true'
              aria-label='Reference Check - links to /?referenceCheck=true'
            >
              Reference Check
            </Link>
          )}
          <Link className={styles.link} to='/create-collection' aria-label='Collections - links to /create-collection'>
            Collections
          </Link>
        </div>
      </div>
      <div>
        <div className={headerLvl.h6}>Resources</div>
        <div className={styles.resources}>
          <Link className={styles.link} to='/pricing'>
            Pricing
          </Link>
          <Link
            className={styles.link}
            href='https://help.researchsolutions.com/hc/en-us/categories/31947397460116-Scite'
            target='_blank'
            rel='noopener noreferrer'
            aria-label='Help & FAQ - Help documentation - links to help.researchsolutions.com (opens in a new tab)'
          >
            Help & FAQ
          </Link>
          <Link
            className={styles.link}
            to='/contact-sales?src=footer'
            aria-label='Request a Demo - links to /contact-sales'
          >
            Request a Demo
          </Link>
          <Link
            className={styles.link}
            href='https://docs.scite.ai'
            target='_blank'
            rel='noopener noreferrer'
            aria-label='API Docs - links to docs.scite.ai (opens in a new tab)'
          >
            API Docs
          </Link>
          <Link
            className={styles.link}
            href='https://scite.ai/llms.txt'
            target='_blank'
            rel='noopener noreferrer'
            aria-label='llms.txt - links to scite.ai/llms.txt (opens in a new tab)'
          >
            llms.txt
          </Link>
          <Link
            className={styles.link}
            to='/blog'
            target='_blank'
            rel='noopener noreferrer'
            aria-label='Blog (opens in a new tab)'
          >
            Blog
          </Link>
        </div>
      </div>
      <div>
        <div className={headerLvl.h6}>Company</div>
        <div className={styles.company}>
          <Link className={styles.link} to='/coverage' aria-label='Data Coverage - links to /coverage'>
            Data Coverage
          </Link>
          <Link
            className={styles.link}
            to='/news-and-press'
            target='_blank'
            rel='noopener noreferrer'
            aria-label='News &amp; Press (opens in a new tab)'
          >
            News & Press
          </Link>
          <Link className={styles.link} to='/jobs'>
            Careers
          </Link>
          <Link className={styles.link} href='mailto:customersupport@researchsolutions.com' aria-label='Contact'>
            Contact
          </Link>
        </div>
      </div>
      <div>
        <div className={headerLvl.h6}>Legal</div>
        <div className={styles.legal}>
          <Link className={styles.link} to='/policy' aria-label='Privacy Policy - links to /policy'>
            Privacy Policy
          </Link>
          <Link className={styles.link} to='/terms' aria-label='Terms of Service - links to /terms'>
            Terms of Service
          </Link>
          <Link
            className={styles.link}
            to='/accessibility-statement'
            aria-label='Accessibility Statement - links to /accessibility-statement'
          >
            Accessibility Statement
          </Link>
          {/* CCPA opt-out the Privacy Policy points to; the `do_not_sell` context enables the CookieModal disclosure */}
          <button
            type='button'
            className={classNames(styles.link, styles.linkButton)}
            onClick={() => dispatch(toggleModal('cookie', true, 'do_not_sell'))}
          >
            Do not sell or share my personal information
          </button>
        </div>
      </div>
      <StatusInfo />
    </div>
  )
}

const StatusInfo = () => (
  <div className={styles.statusInfo}>
    <P>
      <Link
        href={CONFIG.statusPage.url}
        underline
        target='_blank'
        rel='noopener noreferrer'
        aria-label='Status (opens in a new tab)'
      >
        Status
      </Link>
    </P>
  </div>
)

const Copyright = () => (
  <div className={styles.copyrightContainer}>
    <div className={styles.copyrightLayout}>
      <P type='small' className={styles.copyright}>
        Copyright © {new Date().getFullYear()} Scite LLC. All rights reserved.
      </P>
      <P type='small' className={styles.madeWithLove}>
        Made with 💙 for researchers
      </P>

      <div className={styles.rsssFamily}>
        <P type='small' className={styles.rsssBlurb}>
          Part of the Research Solutions Family.
        </P>
        <ResearchSolutionsLogo aria-label='Research Solutions Logo' className={styles.rsssLogo} />
      </div>
    </div>
  </div>
)

export const Footer = () => (
  <footer className={styles.layout}>
    <div className={styles.mainLayout}>
      <About />
      <ProductLinks />
    </div>
    <Copyright />
  </footer>
)

export default Footer
